import React, { useEffect, useRef } from 'react';
import styles from './CareCuesGroupItem.module.scss';
import { RemoveIcon } from '@ax/ui';
import { CareCuesGroupItem as CareCuesGroupItemInterface } from '@ax/redux-store';

interface CareCuesGroupItemProps {
  item: CareCuesGroupItemInterface;
  isEditable?: boolean;
  onEditStarted?: (item: CareCuesGroupItemInterface) => void;
  onEditFinished?: (item: CareCuesGroupItemInterface, newText: string) => void;
  onDelete: (item: CareCuesGroupItemInterface) => void;
  readonly?: boolean;
}

function createRandomString(length: number = 10): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(
    { length },
    () => characters[Math.floor(Math.random() * characters.length)],
  ).join('');
}

const CareCuesGroupItem: React.FC<CareCuesGroupItemProps> = ({
  item,
  onDelete,
  readonly,
  onEditStarted,
  onEditFinished,
  isEditable = false,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [value, setValue] = React.useState(item.displayText);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const handleSwitchToEditMode = () => {
    if (!readonly) {
      setIsEditing(true);
      onEditStarted?.(item);
    }
  };

  const handleFinishEditing = () => {
    setIsEditing(false);
    onEditFinished?.(item, value);
  };

  const adjustTextareaHeight = () => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = `${editorRef.current.scrollHeight}px`;
    }
  };

  const handleEditRequestClick = () => {
    if (isEditable) {
      handleSwitchToEditMode();
    }
  };

  const handleEditRequestKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditRequestClick();
    }
  };

  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.focus();
      editorRef.current.setSelectionRange(0, 0);
      editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      adjustTextareaHeight();
    }
  }, [isEditing, item]);

  return (
    <div className={`${styles.care_cues__group_item} ${isEditing ? styles.editing : ''}`}>
      {isEditing ? (
        <textarea
          ref={editorRef}
          className={styles.care_cues__group_item__editable_text}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleFinishEditing}
        ></textarea>
      ) : (
        <span
          role={isEditable ? 'button' : undefined}
          onClick={handleEditRequestClick}
          onKeyDown={handleEditRequestKeyDown}
          className={styles.care_cues__group_item__display_text}
        >
          {item.displayText?.split('\n').map((line) => (
            <span key={createRandomString()}>
              {line}
              <br />
            </span>
          ))}
        </span>
      )}
      {!readonly && !isEditing && (
        <div className={styles.care_cues__group_item__actions}>
          <button
            aria-label={`Dismiss ${item.name}`}
            className={styles.care_cues__group_item__action}
            onClick={() => onDelete(item)}
          >
            <RemoveIcon
              height={11}
              width={11}
              viewBox="0 0 11 11"
              name={`Dismiss ${item.name}`}
              strokeWidth={0}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default CareCuesGroupItem;
